import {
  type Billing,
  billingOf,
  can,
  newId,
  PAYMENT_METHODS,
  type PaymentMethod,
  type PriceItem,
} from '@quickreport/domain';
import { type LocalDb, queueChange, type Tx } from '@quickreport/sync';
import { recordAudit } from './audit';
import { WorkflowError, type WorkspaceCtx } from './reports';

export interface PaymentRow {
  id: string;
  reportId: string;
  amountPaise: number;
  method: PaymentMethod;
  paidAt: string;
  note: string | null;
}

export interface ReportBilling extends Billing {
  items: PriceItem[];
  payments: PaymentRow[];
}

function methodOf(v: string): PaymentMethod {
  return (PAYMENT_METHODS as readonly string[]).includes(v) ? (v as PaymentMethod) : 'other';
}

/** What a report costs, what has been paid, and each payment. Private to the lab. */
export async function loadBilling(
  db: Pick<LocalDb, 'query'> | Tx,
  reportId: string,
): Promise<ReportBilling | null> {
  const r = await db.query<{
    price_paise: number;
    discount_paise: number;
    price_items: PriceItem[];
  }>('select price_paise, discount_paise, price_items from reports where id = $1', [reportId]);
  const row = r.rows[0];
  if (!row) return null;
  const pays = await db.query<{
    id: string;
    report_id: string;
    amount_paise: number;
    method: string;
    paid_at: string;
    note: string | null;
  }>(
    `select id, report_id, amount_paise, method, paid_at, note from payments
      where report_id = $1 and deleted_at is null order by paid_at, created_at`,
    [reportId],
  );
  const payments = pays.rows.map((p) => ({
    id: p.id,
    reportId: p.report_id,
    amountPaise: Number(p.amount_paise),
    method: methodOf(p.method),
    paidAt: p.paid_at,
    note: p.note,
  }));
  const paid = payments.reduce((n, p) => n + p.amountPaise, 0);
  return {
    ...billingOf(Number(row.price_paise), Number(row.discount_paise), paid),
    items: row.price_items ?? [],
    payments,
  };
}

function needBilling(ctx: WorkspaceCtx) {
  if (!can(ctx.roles, 'manage_billing'))
    throw new WorkflowError('Your role cannot record payments or change prices.');
}

export interface PaymentInput {
  amountPaise: number;
  method?: PaymentMethod;
  paidAt?: string;
  note?: string;
}

/** Record money received for a report, now or any time later. Works offline and syncs. */
export async function addPayment(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportId: string,
  input: PaymentInput,
  now: Date = new Date(),
): Promise<string> {
  needBilling(ctx);
  const amount = Math.round(input.amountPaise);
  if (!(amount > 0)) throw new WorkflowError('Enter an amount more than zero.');
  const at =
    input.paidAt && !Number.isNaN(Date.parse(input.paidAt)) ? input.paidAt : now.toISOString();
  const id = newId();
  await db.transaction(async (tx) => {
    const exists = await tx.query('select 1 from reports where id = $1 and deleted_at is null', [
      reportId,
    ]);
    if (!exists.rows.length) throw new WorkflowError('That report no longer exists.');
    await tx.query(
      `insert into payments (id, org_id, report_id, amount_paise, method, paid_at, note)
       values ($1, $2, $3, $4, $5, $6, $7)`,
      [
        id,
        ctx.orgId,
        reportId,
        amount,
        input.method ?? 'cash',
        new Date(at).toISOString(),
        input.note?.trim() || null,
      ],
    );
    await queueChange(tx, 'payments', id);
    // Filed under the payment, not the report, so money never shows in the report's history.
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'payment',
      entityId: id,
      action: 'payment_added',
      after: { reportId, amountPaise: amount, method: input.method ?? 'cash' },
    });
  });
  return id;
}

/** A wrong entry is removed and entered again; the audit trail keeps what was removed. */
export async function removePayment(db: LocalDb, ctx: WorkspaceCtx, id: string): Promise<void> {
  needBilling(ctx);
  await db.transaction(async (tx) => {
    const r = await tx.query<{ report_id: string; amount_paise: number }>(
      'select report_id, amount_paise from payments where id = $1 and deleted_at is null',
      [id],
    );
    const p = r.rows[0];
    if (!p) return;
    await tx.query('update payments set deleted_at = now(), updated_at = now() where id = $1', [
      id,
    ]);
    await queueChange(tx, 'payments', id);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'payment',
      entityId: id,
      action: 'payment_removed',
      before: { reportId: p.report_id, amountPaise: Number(p.amount_paise) },
    });
  });
}

export interface PricingInput {
  /** Replace the total. The difference is kept as an "Adjustment" line. */
  pricePaise?: number;
  discountPaise?: number;
}

/** Change a report's total or discount. Needs the billing permission. */
export async function setReportPricing(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportId: string,
  input: PricingInput,
): Promise<void> {
  needBilling(ctx);
  await db.transaction(async (tx) => {
    const cur = await loadBilling(tx, reportId);
    if (!cur) throw new WorkflowError('That report no longer exists.');
    let price = cur.pricePaise;
    let items = cur.items;
    if (input.pricePaise !== undefined) {
      price = Math.max(0, Math.round(input.pricePaise));
      const listed = items.filter((i) => i.name !== 'Adjustment');
      const sum = listed.reduce((n, i) => n + i.pricePaise, 0);
      items = price === sum ? listed : [...listed, { name: 'Adjustment', pricePaise: price - sum }];
    }
    const discount = Math.max(0, Math.round(input.discountPaise ?? cur.discountPaise));
    if (discount > price) throw new WorkflowError('The discount cannot be more than the price.');
    if (price === cur.pricePaise && discount === cur.discountPaise) return;
    await tx.query(
      `update reports set price_paise = $2, discount_paise = $3, price_items = $4::jsonb, updated_at = now() where id = $1`,
      [reportId, price, discount, JSON.stringify(items)],
    );
    await queueChange(tx, 'reports', reportId);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'payment',
      entityId: reportId,
      action: 'pricing_changed',
      before: { pricePaise: cur.pricePaise, discountPaise: cur.discountPaise },
      after: { pricePaise: price, discountPaise: discount },
    });
  });
}

/**
 * Money received for a visit that made several reports (a separate culture, for example). It
 * pays each report's balance in turn; anything beyond the total stays on the last report.
 */
export async function addVisitPayment(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportIds: string[],
  input: PaymentInput,
): Promise<void> {
  needBilling(ctx);
  let left = Math.round(input.amountPaise);
  if (!(left > 0) || reportIds.length === 0) return;
  for (const [i, id] of reportIds.entries()) {
    const b = await loadBilling(db, id);
    const last = i === reportIds.length - 1;
    const part = last ? left : Math.min(left, b?.balancePaise ?? 0);
    if (part > 0) await addPayment(db, ctx, id, { ...input, amountPaise: part });
    left -= part;
    if (left <= 0) break;
  }
}
